import React from 'react';
import {
  Paper,
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  Card,
  Collapse,
  IconButton,
  useTheme
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { MdCategory } from 'react-icons/md';
import { purple, green, grey } from '@mui/material/colors';

interface SubcategoryData {
  subcategoryId: string;
  subcategoryName: string;
  assignedDocuments: any[];
  totalDocuments: number;
}

interface CategoryData {
  categoryId: string;
  categoryName: string;
  subcategories: SubcategoryData[];
  totalDocuments: number;
}

interface DocumentCategoriesListProps {
  categoryData: CategoryData[];
  selectedCategory: string | null;
  selectedSubcategory: string | null;
  expandedCategories: Set<string>;
  getCategoryColor: (category: string) => string;
  getCategoryIcon: (category: string) => React.ReactElement;
  handleCategoryToggle: (categoryName: string) => void;
  handleSubcategorySelect: (categoryName: string, subcategoryName: string) => void;
  typography: any;
}

const AssignedDocumentCategoriesList: React.FC<DocumentCategoriesListProps> = ({
  categoryData,
  selectedCategory,
  selectedSubcategory,
  expandedCategories,
  getCategoryColor,
  getCategoryIcon,
  handleCategoryToggle,
  handleSubcategorySelect,
  typography
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        height: 'calc(100vh - 160px)',
        bgcolor: 'white',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MdCategory style={{ color: purple[500], fontSize: 16 }} />
          <Typography
            sx={{
              ...typography.sectionTitle,
              fontWeight: 600
            }}
          >
           Categories
          </Typography>
          <Chip
            label={`${categoryData.length} Available`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: `${purple[500]}15`,
              color: purple[500]
            }}
          />
        </Stack>
      </Box>

      <Divider sx={{ mx: 1.5, opacity: 0.6 }} />

      <Box sx={{ p: 1, overflow: 'auto', height: 'calc(100% - 80px)' }}>
        <Stack spacing={0.25}>
          {categoryData.map((category) => {
            if (!category || !category.categoryName) return null;

            const isExpanded = expandedCategories.has(category.categoryName);
            const categoryColor = getCategoryColor(category.categoryName);

            return (
              <Box key={category.categoryId}>
                {/* Main Category */}
                <Card
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    boxShadow: 1,
                    cursor: 'pointer',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      boxShadow: 2,
                      backgroundColor: `${categoryColor}08`,
                      borderColor: categoryColor
                    },
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => handleCategoryToggle(category.categoryName)}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: `${categoryColor}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: categoryColor
                      }}
                    >
                      {React.cloneElement(getCategoryIcon(category.categoryName) as React.ReactElement<any>, {
                        style: { fontSize: 14 }
                      })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          ...typography.bodyText,
                          fontWeight: 600,
                          lineHeight: 1.2
                        }}
                      >
                        {category.categoryName}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        ...typography.smallText,
                        color: categoryColor,
                        bgcolor: `${categoryColor}15`,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 0.75,
                        minWidth: 16,
                        textAlign: 'center'
                      }}
                    >
                      {category.totalDocuments}
                    </Typography>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                </Card>

                {/* Subcategories */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ ml: 2, mt: 0.5 }}>
                    <Stack spacing={0.25}>
                      {category.subcategories?.map((subcategory) => {
                        if (!subcategory || !subcategory.subcategoryName) return null;

                        const isSelected =
                          selectedCategory === category.categoryName &&
                          selectedSubcategory === subcategory.subcategoryName;

                        return (
                          <Card
                            key={subcategory.subcategoryId}
                            sx={{
                              p: 0.75,
                              borderRadius: 1,
                              boxShadow: isSelected ? 2 : 0.5,
                              cursor: 'pointer',
                              border: isSelected
                                ? `2px solid ${green[500]}`
                                : `1px solid ${theme.palette.divider}`,
                              backgroundColor: isSelected ? `${green[500]}08` : 'background.paper',
                              '&:hover': {
                                boxShadow: 1,
                                backgroundColor: `${green[500]}08`,
                                borderColor: green[500]
                              },
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() =>
                              handleSubcategorySelect(
                                category.categoryName,
                                subcategory.subcategoryName
                              )
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box
                                sx={{
                                  width: 4,
                                  height: 16,
                                  borderRadius: 2,
                                  bgcolor: isSelected ? green[500] : grey[300]
                                }}
                              />
                              <Typography
                                sx={{
                                  ...typography.smallText,
                                  fontWeight: isSelected ? 600 : 400,
                                  flex: 1
                                }}
                              >
                                {subcategory.subcategoryName}
                              </Typography>
                              <Typography
                                sx={{
                                  ...typography.smallText,
                                  color: green[600],
                                  bgcolor: `${green[500]}15`,
                                  px: 0.5,
                                  py: 0.125,
                                  borderRadius: 0.5,
                                  minWidth: 16,
                                  textAlign: 'center',
                                  fontSize: '0.6rem'
                                }}
                              >
                                {subcategory.totalDocuments}
                              </Typography>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};

export default AssignedDocumentCategoriesList;
